import { toCurrency } from 'utilities/currency';

import { useTranslation } from 'react-i18next';
import {
  Item,
  type ItemProps,
} from 'typescript/apps/checkout_v3/components/order_summary/components/item';
import notNullOrUndefined from 'typescript/utilities/not_null_or_undefined';

export enum BillingCycleUnit {
  DAY = 'DAY',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export type ReceiptItemProps = Omit<ItemProps, 'cost'> & {
  cost?: number | string; // overrides ItemProps cost definition
  currency?: string;
  override?: string; // used instead of cost if present
  isTax?: boolean;
  isShipping?: boolean;
  negativeZero?: boolean;
  billingCycle?: BillingCycleUnit;
};

export const ReceiptItem = ({
  label,
  cost,
  currency,
  override,
  isTax = false,
  isShipping = false,
  negativeZero = false,
  billingCycle,
  ...rest
}: ReceiptItemProps) => {
  const { t, i18n } = useTranslation();

  if (!notNullOrUndefined(cost) && !override) {
    return null;
  }

  const costIsZero = cost === 0 || cost === '0.0';

  let value: string;
  if (override) {
    value = override;
  } else {
    value =
      costIsZero && !isTax && !negativeZero && isShipping // zero tax should not display 'free'
        ? t('checkout.label.free')
        : costIsZero && negativeZero
          ? toCurrency('-0.0', currency)
          : toCurrency(cost, currency);
  }

  if (
    billingCycle &&
    i18n.exists(
      `checkout.receipt.label.v3.amountWithBillingCycle.${billingCycle}`,
    )
  ) {
    value = t(
      `checkout.receipt.label.v3.amountWithBillingCycle.${billingCycle}`,
      {
        amount: value,
      },
    );
  }

  return <Item label={label} cost={value} {...rest} size="small" />;
};
